/* Cancelled-after-document flags for Packing List / Bulk Data history:
 * red "N cancelled parcel" badge, "Regenerate" button, Superseded styling. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ban, Loader2, RefreshCw, Undo2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";

export const useCancelFlags = (tool, refreshKey) => {
  const [flags, setFlags] = useState({});
  const load = useCallback(() => {
    axios.get(`${API}/packing-list/cancel-flags?tool=${tool}`, { headers: getAuthHeader() })
      .then(r => setFlags(r.data || {})).catch(() => {});
  }, [tool]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("order-register:invoice-changed", h);
    return () => window.removeEventListener("order-register:invoice-changed", h);
  }, [load]);
  return [flags, load];
};

const tip = (list, verb) => list.map(p =>
  `${p.name || "?"}${p.tracking ? ` · ${p.tracking}` : ""} — ${verb} by ${p.by || "?"}${p.at ? ` on ${new Date(p.at).toLocaleString()}` : ""}${p.reason ? ` · ${p.reason}` : ""}`).join("\n");

export const CancelFlagBadges = ({ entry, flags, onRegenerated, testId }) => {
  const [busy, setBusy] = useState(false);
  const f = flags[entry.id];
  if (entry.superseded_by) {
    return (
      <span className="ml-1 inline-flex items-center rounded-full bg-slate-200 text-slate-600 px-1.5 text-[9px] font-bold uppercase"
            title={`Superseded${entry.superseded_by_name ? ` by ${entry.superseded_by_name}` : ""}${entry.superseded_at ? ` on ${new Date(entry.superseded_at).toLocaleString()}` : ""} — kept for records`}
            data-testid={`superseded-badge-${testId}-${entry.id}`}>Superseded</span>);
  }
  if (!f && !(entry.excluded_parcels || []).length) return null;
  const regen = async () => {
    if (!window.confirm(`Regenerate Packing List + Bulk Data for invoice ${entry.invoice_no} from the Order Register?\nThe current files are kept as "Superseded".`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/packing-list/regen-without-cancelled`, { invoice_no: entry.invoice_no },
        { headers: getAuthHeader() });
      toast.success(`Regenerated ${r.data.invoice_no}${r.data.excluded ? ` — ${r.data.excluded} cancelled parcel(s) left out` : ""}`);
      if (r.data.bulk_data_error) toast.warning(`Bulk Data: ${r.data.bulk_data_error}`);
      onRegenerated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); } finally { setBusy(false); }
  };
  const nC = f?.cancelled?.length || 0;
  const nR = f?.restored?.length || 0;
  const excl = entry.excluded_parcels || [];
  return (
    <span className="ml-1 inline-flex flex-wrap items-center gap-1 align-middle">
      {nC > 0 && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-600 text-white px-1.5 text-[9px] font-bold"
              title={tip(f.cancelled, "cancelled")} data-testid={`cancel-flag-${testId}-${entry.id}`}>
          <Ban className="w-2.5 h-2.5" />{nC} cancelled parcel{nC > 1 ? "s" : ""}
        </span>)}
      {nR > 0 && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500 text-white px-1.5 text-[9px] font-bold"
              title={tip(f.restored, "was cancelled")} data-testid={`restore-flag-${testId}-${entry.id}`}>
          <Undo2 className="w-2.5 h-2.5" />{nR} restored parcel{nR > 1 ? "s" : ""}
        </span>)}
      {!nC && !nR && excl.length > 0 && (
        <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 px-1.5 text-[9px] font-bold"
              title={`Regenerated without:\n${tip(excl, "cancelled")}`} data-testid={`excluded-flag-${testId}-${entry.id}`}>
          −{excl.length} cancelled removed
        </span>)}
      {(nC > 0 || nR > 0) && (
        <button type="button" onClick={regen} disabled={busy}
                className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white ${nC ? "bg-rose-700 hover:bg-rose-800" : "bg-amber-600 hover:bg-amber-700"}`}
                data-testid={`regen-cancelled-${testId}-${entry.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {nC ? "Regenerate without cancelled" : "Regenerate with restored"}
        </button>)}
    </span>
  );
};

export const SUPERSEDED_ROW_CLS = "opacity-60 line-through decoration-slate-400";

/* CORRECTED (new file after a changed reopen) / Replaced (old file kept for records) tags beside the file name. */
export const CorrectedTag = ({ entry, testId }) => entry.corrected ? (
  <span className="ml-1 inline-flex items-center rounded bg-amber-500 px-1 py-px align-middle text-[9px] font-bold text-white"
        title={`Corrected${entry.corrected_by ? ` by ${entry.corrected_by}` : ""}${entry.corrected_at ? ` · ${new Date(entry.corrected_at).toLocaleString()}` : ""}${(entry.corrected_changes || []).length ? `\n${entry.corrected_changes.join("\n")}` : ""}`}
        data-testid={`corrected-badge-${testId}-${entry.id}`}>CORRECTED</span>
) : entry.superseded_by ? (
  <span className="ml-1 inline-flex items-center rounded border border-slate-300 bg-slate-100 px-1 py-px align-middle text-[9px] font-bold text-slate-600"
        title={`Replaced${entry.superseded_by_name ? ` by ${entry.superseded_by_name}` : ""}${entry.superseded_at ? ` · ${new Date(entry.superseded_at).toLocaleString()}` : ""}`}
        data-testid={`replaced-badge-${testId}-${entry.id}`}>REPLACED</span>
) : null;

const SUP_TTL = 10 * 60 * 1000;
/* Show/hide Superseded rows; remembered, auto-hides after 10 min. */
export const useSupersededToggle = (tool) => {
  const k = `showSuperseded:${tool}`;
  const read = () => { try { const v = JSON.parse(localStorage.getItem(k) || "null"); return !!v && Date.now() - v.at < SUP_TTL; } catch { return false; } };
  const [on, setOn] = useState(read);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => { if (!read()) setOn(false); }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [on]);
  const toggle = () => {
    const nv = !on;
    localStorage.setItem(k, JSON.stringify(nv ? { at: Date.now() } : null));
    setOn(nv);
  };
  return [on, toggle];
};

export const SupersededToggle = ({ on, toggle, count, testId }) => (
  <button type="button" onClick={toggle} data-testid={`superseded-toggle-${testId}`}
          title={on ? "Hide old superseded files (auto-hides after 10 min)" : "Show old superseded files kept for records"}
          className={`h-7 px-2.5 rounded-full border text-[11px] font-semibold transition-colors ${on
            ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
    {on ? "Hide" : "Show"} superseded ({count})
  </button>
);
